/**
 * SCI Field QC — API client.
 *
 * One function per route in ./api-contract.ts, and nothing that is not a route
 * in that file. Every call goes through `call()`, which:
 *
 *   1. Looks the route up in API_ROUTES so the HTTP method and the required
 *      role come from the contract, never from a string literal here.
 *   2. Refuses client-side to issue a mutating request from a client (read
 *      only) session. The server enforces this too — that is the real gate —
 *      but the spec also requires that a client session issues *no* mutating
 *      calls at all, so the guard lives here as well as in the UI.
 *   3. Normalises every failure into an `ApiError` carrying the contract's
 *      error code, field errors and (for a blocked close) blocking_items.
 *
 * It also owns the two pieces of resilience the spec calls for:
 *
 *   - `save()` wraps a mutating call so that a failure NEVER discards the
 *     caller's draft: the failure is recorded in `unsavedChanges` with a
 *     retry closure, and `save()` resolves to null so the caller keeps its
 *     local state.
 *   - `queuePhotoUpload()` downscales on-device, uploads to R2 via the signed
 *     URL and attaches the key, retrying in the background. A photo never
 *     silently vanishes; it shows as pending until it lands.
 */

import { get, writable, derived, type Readable } from 'svelte/store';
import {
  API_ROUTES,
  apiPath,
  roleSatisfies,
  type EquipmentOptionsResponse,
  type MaterialOptionsResponse,
  type ActivityOptionsResponse,
  type PersonnelOptionsResponse,
  type RouteDef,
  type RouteName
} from './api-contract';
import {
  type ApiErrorBody,
  type ApiErrorCode,
  type AttachPhotoRequest,
  type AuditListResponse,
  type ChuteTestListResponse,
  type ChuteTestResponse,
  type CancellationFee,
  type CancellationFeeListResponse,
  type CreateCancellationFeeRequest,
  type CreateChuteTestRequest,
  type CreateEquipmentRateRequest,
  type CreateMaterialPriceRequest,
  type CreateActivityRateRequest,
  type ArchiveActivityRequest,
  type ActivityRate,
  type ActivityRateListResponse,
  type CreateMixPriceRequest,
  type CreateOperatorRateRequest,
  type CreatePourRequest,
  type CreateShareTokenRequest,
  type CreateStopHoldEventRequest,
  type ArchiveWorkerRequest,
  type ArchiveCementCountRequest,
  type ArchiveMaterialRequest,
  type CementInventoryResponse,
  type ArchiveMixRequest,
  type ArchiveTruckingRequest,
  type ArchiveEquipmentRequest,
  type ArchiveOperatorRequest,
  type CreateWorkerRateRequest,
  type ContractReportListResponse,
  type ContractReportResponse,
  type DailyReportListResponse,
  type DailyReportPhotoListResponse,
  type DailyReportPhotoResponse,
  type DailyReportResponse,
  type EndOfPourItemListResponse,
  type EndOfPourItemResponse,
  type EquipmentLease,
  type Expense,
  type ExpenseCategory,
  type ExpenseCategoryListResponse,
  type ExpenseMonthResponse,
  type LeaseListResponse,
  type SaveExpenseCategoryRequest,
  type SaveExpenseRequest,
  type SaveLeaseRequest,
  type CheckStatementRequest,
  type CheckStatementResponse,
  type ImportStatementRequest,
  type StatementDetailResponse,
  type StatementImportResponse,
  type StatementListResponse,
  type EquipmentInspectionListResponse,
  type EquipmentInspectionResponse,
  type EquipmentRate,
  type EquipmentRateListResponse,
  type FieldTestLogListResponse,
  type FieldTestLogResponse,
  type AdminSitesResponse,
  type BreakdownResponse,
  type ItpItem,
  type ItpItemResponse,
  type JhaListResponse,
  type JhaResponse,
  type JobSetupItemResponse,
  type JobSetupResponse,
  type JobSite,
  type AccountArea,
  type LoginRequest,
  type LoginResponse,
  type MaterialPrice,
  type MaterialPriceListResponse,
  type DismissNameRequest,
  type RateGapsResponse,
  type MixDesignListResponse,
  type MixDesignOptionsResponse,
  type MixDesignPrice,
  type MixDesignResponse,
  type MixPriceListResponse,
  type OkResponse,
  type OperatorRate,
  type OperatorRateListResponse,
  type PhotoListResponse,
  type PhotoResponse,
  type PhotoUploadUrlRequest,
  type PhotoUploadUrlResponse,
  type PnlBreakdownResponse,
  type PnlDashboardResponse,
  type PourListResponse,
  type TrackedMonthsResponse,
  type PourResponse,
  type ProjectListResponse,
  type ProjectResponse,
  type Role,
  type SaveContractReportRequest,
  type SaveDailyReportRequest,
  type SaveEquipmentInspectionRequest,
  type SaveFieldTestLogRequest,
  type SaveJhaRequest,
  type SaveMixDesignRequest,
  type SaveCementCountRequest,
  type SaveSiteRequest,
  type SessionResponse,
  type SetShiftRequest,
  type ShareLoginRequest,
  type ShareTokenListResponse,
  type ShareTokenResponse,
  type SiteListResponse,
  type SiteResponse,
  type UpdateChuteTestRequest,
  type UpdateEndOfPourItemRequest,
  type UpdateItpItemRequest,
  type UpdateJobSetupItemRequest,
  type UpdateJobSetupRequest,
  type UpdatePourRequest,
  type UpdateProjectRequest,
  type UpdateStopHoldEventRequest,
  type StopHoldEventListResponse,
  type StopHoldEventResponse,
  type Timestamp,
  type CreateTaxRateRequest,
  type CreateTruckingRateRequest,
  type TaxRate,
  type TaxRateListResponse,
  type TruckingRate,
  type TruckingRateListResponse,
  type WorkerRate,
  type WorkerRateListResponse
} from './types';

/* ==========================================================================
 * Errors
 * ======================================================================== */

/** Every failure the client raises, network faults included. */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;
  readonly blockingItems: ItpItem[] | undefined;
  /** True when the request never reached the server (offline, DNS, abort). */
  readonly offline: boolean;

  constructor(
    code: ApiErrorCode,
    message: string,
    options: {
      status?: number;
      fields?: Record<string, string>;
      blockingItems?: ItpItem[];
      offline?: boolean;
    } = {}
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = options.status ?? 0;
    this.fields = options.fields;
    this.blockingItems = options.blockingItems;
    this.offline = options.offline ?? false;
  }
}

/** Narrowing helper for `catch (err)`. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/* ==========================================================================
 * Session role — set by the store, read by the guard
 * ======================================================================== */

let sessionRole: Role | null = null;
let sessionArea: AccountArea | null = null;

/**
 * Told to the client by the store whenever the session changes, so that the
 * guard below can refuse a mutating call before it is issued. Not a store, to
 * avoid an import cycle with ./store.ts.
 */
export function setSessionRole(role: Role | null, area: AccountArea | null = null): void {
  sessionRole = role;
  sessionArea = area;
}

export function currentRole(): Role | null {
  return sessionRole;
}

/** Callbacks fired when the server says the session is gone. */
type UnauthorizedHandler = () => void;
const unauthorizedHandlers = new Set<UnauthorizedHandler>();

/**
 * Register a handler for 401 responses. The store uses this to drop back to
 * the login screen — WITHOUT touching any draft, so re-authenticating
 * preserves in-progress work as the spec requires.
 */
export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
}

/* ==========================================================================
 * Transport
 * ======================================================================== */

/** True while at least one request is in flight (for the app-bar spinner). */
export const inFlight = writable(0);

/** Last observed connectivity, updated on every call. */
export const online = writable(typeof navigator === 'undefined' ? true : navigator.onLine);

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => online.set(true));
  window.addEventListener('offline', () => online.set(false));
}

async function parseError(response: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = null;
  }
  const err = body?.error;
  const code: ApiErrorCode =
    err?.code ??
    (response.status === 401
      ? 'unauthorized'
      : response.status === 403
        ? 'forbidden'
        : response.status === 404
          ? 'not_found'
          : response.status === 409
            ? 'conflict'
            : response.status === 429
              ? 'rate_limited'
              : 'internal_error');

  return new ApiError(code, err?.message ?? `Request failed (${response.status}).`, {
    status: response.status,
    fields: err?.fields,
    blockingItems: err?.blocking_items
  });
}

interface CallOptions {
  body?: unknown;
  /**
   * A raw binary body — the request IS these bytes, sent with the blob's own
   * Content-Type instead of JSON. Used by the Daily Report photo upload,
   * where the body is the downscaled image itself.
   */
  blob?: Blob;
  /** Overrides the path built from the route name (for parameterised paths). */
  signal?: AbortSignal;
}

/**
 * The single request funnel. `name` selects the method and required role from
 * the contract; `path` is built with `apiPath`.
 */
async function call<T>(name: RouteName, path: string, options: CallOptions = {}): Promise<T> {
  // Widened to RouteDef: indexed access on the literal union only exposes
  // fields every route shares, and requiresArea is present on some but not
  // all of them.
  const route: RouteDef = API_ROUTES[name];

  if (!roleSatisfies(route.requiresRole, sessionRole, sessionArea, route.requiresArea)) {
    // Guard covers five cases: signed out; a non-office session reaching a mix
    // design route; an office session reaching a field QC route; an office
    // session whose area does not match the route's requiresArea; or a client
    // session attempting a mutating call. The server enforces these too, but
    // the spec requires the client guard as well.
    const code: ApiErrorCode = sessionRole === null ? 'unauthorized' : 'forbidden';
    const message =
      sessionRole === null
        ? 'You are signed out.'
        : route.requiresRole === 'office'
          ? 'Sign in with an office login to do that.'
          : sessionRole === 'office'
            ? 'The mix design login cannot open the field QC record.'
            : 'This session is read-only. Ask an SCI crew member to make this change.';
    throw new ApiError(code, message, { status: sessionRole === null ? 401 : 403 });
  }

  const init: RequestInit = {
    method: route.method,
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal: options.signal
  };

  if (options.blob !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': options.blob.type || 'application/octet-stream' };
    init.body = options.blob;
  } else if (options.body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(options.body);
  }

  inFlight.update((n) => n + 1);
  let response: Response;
  try {
    response = await fetch(path, init);
  } catch {
    online.set(false);
    throw new ApiError('internal_error', 'No connection. Your work is saved on this device.', {
      offline: true
    });
  } finally {
    inFlight.update((n) => Math.max(0, n - 1));
  }

  online.set(true);

  if (!response.ok) {
    const error = await parseError(response);
    if (error.status === 401) {
      sessionRole = null;
      for (const handler of unauthorizedHandlers) handler();
    }
    throw error;
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/* ==========================================================================
 * Unsaved work — the draft is never discarded because a request failed
 * ======================================================================== */

export interface UnsavedChange {
  /** Stable key, one per editable section/row (e.g. 'pour:7:header'). */
  key: string;
  /** Human label shown in the unsaved bar, e.g. 'Pour header'. */
  label: string;
  error: ApiError;
  at: Timestamp;
  attempts: number;
  /** Re-runs the exact same save. */
  retry: () => Promise<unknown>;
}

const unsavedStore = writable<UnsavedChange[]>([]);

/** Sections whose last save failed. The UI shows a retry for each. */
export const unsavedChanges: Readable<UnsavedChange[]> = { subscribe: unsavedStore.subscribe };

/** True when anything is unsaved. */
export const hasUnsaved: Readable<boolean> = derived(unsavedStore, (list) => list.length > 0);

/** Number of saves currently being retried or flushed. */
export const savingCount = writable(0);

export const isSaving: Readable<boolean> = derived(savingCount, (n) => n > 0);

function clearUnsaved(key: string): void {
  unsavedStore.update((list) => list.filter((entry) => entry.key !== key));
}

function recordUnsaved(entry: UnsavedChange): void {
  unsavedStore.update((list) => {
    const previous = list.find((item) => item.key === entry.key);
    const next = list.filter((item) => item.key !== entry.key);
    next.push({ ...entry, attempts: (previous?.attempts ?? 0) + 1 });
    return next;
  });
}

/**
 * Run a mutating call while retaining the caller's draft on failure.
 *
 * Resolves to the response on success (and clears any prior unsaved marker for
 * `key`), or to `null` on failure — at which point the caller MUST keep its
 * local draft, because it is now the only copy of that data. The failure is
 * published on `unsavedChanges` with a `retry()` that re-runs this same save.
 *
 * It deliberately does not throw: a thrown save turns into an error boundary
 * or a dialog, and the spec forbids anything that can trap a user mid-pour.
 */
export async function save<T>(
  key: string,
  label: string,
  run: () => Promise<T>
): Promise<T | null> {
  savingCount.update((n) => n + 1);
  try {
    const result = await run();
    clearUnsaved(key);
    return result;
  } catch (cause) {
    const error = isApiError(cause)
      ? cause
      : new ApiError('internal_error', cause instanceof Error ? cause.message : 'Save failed.');
    recordUnsaved({
      key,
      label,
      error,
      at: new Date().toISOString(),
      attempts: 0,
      retry: () => save(key, label, run)
    });
    return null;
  } finally {
    savingCount.update((n) => Math.max(0, n - 1));
  }
}

/** Retry every failed save, oldest first. */
export async function retryAllUnsaved(): Promise<void> {
  const pending = get(unsavedStore);
  for (const entry of pending) {
    await entry.retry();
  }
}

/** Drop an unsaved marker without retrying (used when the row is deleted). */
export function forgetUnsaved(key: string): void {
  clearUnsaved(key);
}

/* ==========================================================================
 * Routes
 * ======================================================================== */

export const api = {
  /* ---------------------------------------------------------------- auth */

  /** Never 401s; returns { session: null } when signed out. */
  getSession(): Promise<SessionResponse> {
    return call<SessionResponse>('session', apiPath.session());
  },

  login(body: LoginRequest): Promise<LoginResponse> {
    return call<LoginResponse>('login', apiPath.login(), { body });
  },

  shareLogin(body: ShareLoginRequest): Promise<LoginResponse> {
    return call<LoginResponse>('shareLogin', apiPath.shareLogin(), { body });
  },

  logout(): Promise<OkResponse> {
    return call<OkResponse>('logout', apiPath.logout());
  },

  setShift(body: SetShiftRequest): Promise<LoginResponse> {
    return call<LoginResponse>('setShift', apiPath.setShift(), { body });
  },

  /* -------------------------------------------------------- mix designs */

  listMixDesigns(): Promise<MixDesignListResponse> {
    return call<MixDesignListResponse>('listMixDesigns', apiPath.mixDesigns());
  },

  createMixDesign(body: SaveMixDesignRequest): Promise<MixDesignResponse> {
    return call<MixDesignResponse>('createMixDesign', apiPath.mixDesigns(), { body });
  },

  getMixDesign(mixId: number): Promise<MixDesignResponse> {
    return call<MixDesignResponse>('getMixDesign', apiPath.mixDesign(mixId));
  },

  updateMixDesign(mixId: number, body: SaveMixDesignRequest): Promise<MixDesignResponse> {
    return call<MixDesignResponse>('updateMixDesign', apiPath.mixDesign(mixId), { body });
  },

  deleteMixDesign(mixId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteMixDesign', apiPath.mixDesign(mixId));
  },

  /* --------------------------------------------------------- quality: JHA */

  listJha(siteId?: number | null): Promise<JhaListResponse> {
    const path = siteId != null ? `${apiPath.jhaRecords()}?site=${siteId}` : apiPath.jhaRecords();
    return call<JhaListResponse>('listJha', path);
  },

  createJha(body: SaveJhaRequest): Promise<JhaResponse> {
    return call<JhaResponse>('createJha', apiPath.jhaRecords(), { body });
  },

  getJha(jhaId: number): Promise<JhaResponse> {
    return call<JhaResponse>('getJha', apiPath.jhaRecord(jhaId));
  },

  updateJha(jhaId: number, body: SaveJhaRequest): Promise<JhaResponse> {
    return call<JhaResponse>('updateJha', apiPath.jhaRecord(jhaId), { body });
  },

  deleteJha(jhaId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteJha', apiPath.jhaRecord(jhaId));
  },

  /* ----------------------------------------------- quality: field tests */

  listFieldTests(siteId?: number | null): Promise<FieldTestLogListResponse> {
    const path =
      siteId != null ? `${apiPath.fieldTestLogs()}?site=${siteId}` : apiPath.fieldTestLogs();
    return call<FieldTestLogListResponse>('listFieldTests', path);
  },

  createFieldTest(body: SaveFieldTestLogRequest): Promise<FieldTestLogResponse> {
    return call<FieldTestLogResponse>('createFieldTest', apiPath.fieldTestLogs(), { body });
  },

  getFieldTest(fieldTestId: number): Promise<FieldTestLogResponse> {
    return call<FieldTestLogResponse>('getFieldTest', apiPath.fieldTestLog(fieldTestId));
  },

  updateFieldTest(
    fieldTestId: number,
    body: SaveFieldTestLogRequest
  ): Promise<FieldTestLogResponse> {
    return call<FieldTestLogResponse>('updateFieldTest', apiPath.fieldTestLog(fieldTestId), {
      body
    });
  },

  deleteFieldTest(fieldTestId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteFieldTest', apiPath.fieldTestLog(fieldTestId));
  },

  /* -------------------------------------------------- quality: equipment */

  listEquipment(siteId?: number | null): Promise<EquipmentInspectionListResponse> {
    const path =
      siteId != null
        ? `${apiPath.equipmentInspections()}?site=${siteId}`
        : apiPath.equipmentInspections();
    return call<EquipmentInspectionListResponse>('listEquipment', path);
  },

  createEquipment(body: SaveEquipmentInspectionRequest): Promise<EquipmentInspectionResponse> {
    return call<EquipmentInspectionResponse>(
      'createEquipment',
      apiPath.equipmentInspections(),
      { body }
    );
  },

  getEquipment(equipmentId: number): Promise<EquipmentInspectionResponse> {
    return call<EquipmentInspectionResponse>(
      'getEquipment',
      apiPath.equipmentInspection(equipmentId)
    );
  },

  updateEquipment(
    equipmentId: number,
    body: SaveEquipmentInspectionRequest
  ): Promise<EquipmentInspectionResponse> {
    return call<EquipmentInspectionResponse>(
      'updateEquipment',
      apiPath.equipmentInspection(equipmentId),
      { body }
    );
  },

  deleteEquipment(equipmentId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteEquipment', apiPath.equipmentInspection(equipmentId));
  },

  /* --------------------------------------------- quality: daily reports */

  listDailyReports(siteId?: number | null): Promise<DailyReportListResponse> {
    const path =
      siteId != null ? `${apiPath.dailyReports()}?site=${siteId}` : apiPath.dailyReports();
    return call<DailyReportListResponse>('listDailyReports', path);
  },

  createDailyReport(body: SaveDailyReportRequest): Promise<DailyReportResponse> {
    return call<DailyReportResponse>('createDailyReport', apiPath.dailyReports(), { body });
  },

  getDailyReport(dailyReportId: number): Promise<DailyReportResponse> {
    return call<DailyReportResponse>('getDailyReport', apiPath.dailyReport(dailyReportId));
  },

  updateDailyReport(
    dailyReportId: number,
    body: SaveDailyReportRequest
  ): Promise<DailyReportResponse> {
    return call<DailyReportResponse>('updateDailyReport', apiPath.dailyReport(dailyReportId), {
      body
    });
  },

  listDailyReportPhotos(dailyReportId: number): Promise<DailyReportPhotoListResponse> {
    return call<DailyReportPhotoListResponse>(
      'listDailyReportPhotos',
      apiPath.dailyReportPhotos(dailyReportId)
    );
  },

  /** The body IS the image — downscale it on-device first (lib/photo.ts's downscalePhoto). Idempotent: the same bytes twice return the existing photo. */
  uploadDailyReportPhoto(dailyReportId: number, image: Blob): Promise<DailyReportPhotoResponse> {
    return call<DailyReportPhotoResponse>(
      'uploadDailyReportPhoto',
      apiPath.dailyReportPhotos(dailyReportId),
      { blob: image }
    );
  },

  deleteDailyReportPhoto(photoId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteDailyReportPhoto', apiPath.dailyReportPhoto(photoId));
  },

  deleteDailyReport(dailyReportId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteDailyReport', apiPath.dailyReport(dailyReportId));
  },

  /* -------------------------------------------- quality: contract reports */

  listContractReports(siteId?: number | null): Promise<ContractReportListResponse> {
    const path =
      siteId != null ? `${apiPath.contractReports()}?site=${siteId}` : apiPath.contractReports();
    return call<ContractReportListResponse>('listContractReports', path);
  },

  createContractReport(body: SaveContractReportRequest): Promise<ContractReportResponse> {
    return call<ContractReportResponse>('createContractReport', apiPath.contractReports(), { body });
  },

  getContractReport(contractReportId: number): Promise<ContractReportResponse> {
    return call<ContractReportResponse>('getContractReport', apiPath.contractReport(contractReportId));
  },

  updateContractReport(
    contractReportId: number,
    body: SaveContractReportRequest
  ): Promise<ContractReportResponse> {
    return call<ContractReportResponse>(
      'updateContractReport',
      apiPath.contractReport(contractReportId),
      { body }
    );
  },

  deleteContractReport(contractReportId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteContractReport', apiPath.contractReport(contractReportId));
  },

  listEquipmentOptions(): Promise<EquipmentOptionsResponse> {
    return call<EquipmentOptionsResponse>('qualityEquipmentOptions', apiPath.equipmentOptions());
  },

  listMixDesignOptions(): Promise<MixDesignOptionsResponse> {
    return call<MixDesignOptionsResponse>('qualityMixDesignOptions', apiPath.mixDesignOptions());
  },

  /** With a site, that site's own materials join the defaults. */
  listMaterialOptions(siteId?: number | null): Promise<MaterialOptionsResponse> {
    const path = siteId != null ? `${apiPath.materialOptions()}?site=${siteId}` : apiPath.materialOptions();
    return call<MaterialOptionsResponse>('qualityMaterialOptions', path);
  },

  listActivityOptions(siteId?: number | null): Promise<ActivityOptionsResponse> {
    const path = siteId != null ? `${apiPath.activityOptions()}?site=${siteId}` : apiPath.activityOptions();
    return call<ActivityOptionsResponse>('qualityActivityOptions', path);
  },

  listPersonnelOptions(): Promise<PersonnelOptionsResponse> {
    return call<PersonnelOptionsResponse>('qualityPersonnelOptions', apiPath.personnelOptions());
  },

  /* ------------------------------------------------------------- sites */

  listSites(): Promise<SiteListResponse> {
    return call<SiteListResponse>('listSites', apiPath.sites());
  },
  createSite(body: SaveSiteRequest): Promise<JobSite> {
    return call<JobSite>('createSite', apiPath.sites(), { body });
  },
  getSite(siteId: number): Promise<SiteResponse> {
    return call<SiteResponse>('getSite', apiPath.site(siteId));
  },
  updateSite(siteId: number, body: SaveSiteRequest): Promise<JobSite> {
    return call<JobSite>('updateSite', apiPath.site(siteId), { body });
  },
  deleteSite(siteId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteSite', apiPath.site(siteId));
  },

  /* -------------------------------------------------- cement inventory */
  getCementInventory(siteId: number): Promise<CementInventoryResponse> {
    return call<CementInventoryResponse>('getCementInventory', apiPath.cementInventory(siteId));
  },
  createCementCount(body: SaveCementCountRequest): Promise<CementInventoryResponse> {
    return call<CementInventoryResponse>('createCementCount', apiPath.cementCounts(), { body });
  },
  archiveCementCount(body: ArchiveCementCountRequest): Promise<CementInventoryResponse> {
    return call<CementInventoryResponse>('archiveCementCount', apiPath.cementCountArchive(), { body });
  },
  adminSites(): Promise<AdminSitesResponse> {
    return call<AdminSitesResponse>('adminSites', apiPath.adminSites());
  },

  /* ------------------------------------------------------- admin: P&L */

  /** Exactly one of day / week / month. A week is any date in the Monday-to-Sunday week meant. */
  getPnl(scope: { day?: string; week?: string; month?: string }): Promise<PnlDashboardResponse> {
    const params = new URLSearchParams();
    if (scope.day) params.set('day', scope.day);
    else if (scope.week) params.set('week', scope.week);
    else params.set('month', scope.month ?? '');
    return call<PnlDashboardResponse>('adminPnl', `${apiPath.adminPnl()}?${params.toString()}`);
  },

  /** `siteId` keys the linked group; without it the name is the unlinked text group. */
  getPnlProject(
    name: string,
    scope: { day?: string; week?: string; month?: string },
    siteId?: number | null
  ): Promise<PnlBreakdownResponse> {
    const params = new URLSearchParams({ name });
    if (scope.day) params.set('day', scope.day);
    else if (scope.week) params.set('week', scope.week);
    else params.set('month', scope.month ?? '');
    if (siteId !== undefined && siteId !== null) params.set('site', String(siteId));
    return call<PnlBreakdownResponse>('adminPnlProject', `${apiPath.adminPnlProject()}?${params.toString()}`);
  },

  /**
   * What the crew costed out, month by month — the Tracked chart's source.
   *
   * `to` is required rather than defaulted: the server refuses a run of
   * months with no end, deliberately, so a chart is never drawn for a period
   * nobody asked for. The caller always knows which month it is looking at,
   * so it is the caller that says.
   */
  getTrackedMonths(to: string, count: number, siteId?: number | null): Promise<TrackedMonthsResponse> {
    const params = new URLSearchParams({ to, count: String(count) });
    if (siteId !== undefined && siteId !== null) params.set('site', String(siteId));
    return call<TrackedMonthsResponse>('adminPnlMonths', `${apiPath.adminPnlMonths()}?${params.toString()}`);
  },

  /* --------------------------------------- admin: Total Breakdown */

  /**
   * Actual dollars in and out, from imported statements. Exactly one of
   * month / year — the server refuses both, so the caller picks one here
   * rather than discovering it in a 422.
   */
  getBreakdown(
    period: { month?: string; year?: string },
    siteId?: number | null
  ): Promise<BreakdownResponse> {
    const params = new URLSearchParams();
    if (period.month) params.set('month', period.month);
    else if (period.year) params.set('year', period.year);
    if (siteId !== undefined && siteId !== null) params.set('site', String(siteId));
    const qs = params.toString();
    return call<BreakdownResponse>('adminBreakdown', `${apiPath.adminBreakdown()}${qs ? `?${qs}` : ''}`);
  },

  /**
   * The same actual dollars over a job's — or the company's — WHOLE recorded
   * life, with no period at all. A separate function from `getBreakdown`
   * rather than a third key on its period argument, because it is a different
   * question: money reaches the bank weeks after the pour, so what a job has
   * collected and what it is still owed are not monthly quantities and would
   * read as a disaster inside any one month.
   */
  getBreakdownToDate(siteId?: number | null): Promise<BreakdownResponse> {
    const params = new URLSearchParams({ span: 'to_date' });
    if (siteId !== undefined && siteId !== null) params.set('site', String(siteId));
    return call<BreakdownResponse>('adminBreakdown', `${apiPath.adminBreakdown()}?${params.toString()}`);
  },

  /* ---------------------------------------------------- admin: prices */

  listRateGaps(): Promise<RateGapsResponse> {
    return call<RateGapsResponse>('adminRateGaps', apiPath.adminRateGaps());
  },

  dismissName(body: DismissNameRequest): Promise<RateGapsResponse> {
    return call<RateGapsResponse>('adminDismissName', apiPath.adminDismissName(), { body });
  },

  listMaterialPrices(): Promise<MaterialPriceListResponse> {
    return call<MaterialPriceListResponse>('listMaterialPrices', apiPath.materialPrices());
  },
  createMaterialPrice(body: CreateMaterialPriceRequest): Promise<MaterialPrice> {
    return call<MaterialPrice>('createMaterialPrice', apiPath.materialPrices(), { body });
  },
  archiveMaterial(body: ArchiveMaterialRequest): Promise<MaterialPriceListResponse> {
    return call<MaterialPriceListResponse>('archiveMaterial', apiPath.materialArchive(), { body });
  },
  listActivityRates(): Promise<ActivityRateListResponse> {
    return call<ActivityRateListResponse>('listActivityRates', apiPath.activityRates());
  },
  createActivityRate(body: CreateActivityRateRequest): Promise<ActivityRate> {
    return call<ActivityRate>('createActivityRate', apiPath.activityRates(), { body });
  },
  archiveActivity(body: ArchiveActivityRequest): Promise<ActivityRateListResponse> {
    return call<ActivityRateListResponse>('archiveActivity', apiPath.activityArchive(), { body });
  },

  listWorkerRates(): Promise<WorkerRateListResponse> {
    return call<WorkerRateListResponse>('listWorkerRates', apiPath.workerRates());
  },
  createWorkerRate(body: CreateWorkerRateRequest): Promise<WorkerRate> {
    return call<WorkerRate>('createWorkerRate', apiPath.workerRates(), { body });
  },
  archiveWorker(body: ArchiveWorkerRequest): Promise<WorkerRateListResponse> {
    return call<WorkerRateListResponse>('archiveWorker', apiPath.workerArchive(), { body });
  },

  listMixPrices(): Promise<MixPriceListResponse> {
    return call<MixPriceListResponse>('listMixPrices', apiPath.mixPrices());
  },
  createMixPrice(body: CreateMixPriceRequest): Promise<MixDesignPrice> {
    return call<MixDesignPrice>('createMixPrice', apiPath.mixPrices(), { body });
  },
  archiveMix(body: ArchiveMixRequest): Promise<MixPriceListResponse> {
    return call<MixPriceListResponse>('archiveMix', apiPath.mixArchive(), { body });
  },

  listTruckingRates(): Promise<TruckingRateListResponse> {
    return call<TruckingRateListResponse>('listTruckingRates', apiPath.truckingRates());
  },
  createTruckingRate(body: CreateTruckingRateRequest): Promise<TruckingRate> {
    return call<TruckingRate>('createTruckingRate', apiPath.truckingRates(), { body });
  },
  archiveTrucking(body: ArchiveTruckingRequest): Promise<TruckingRateListResponse> {
    return call<TruckingRateListResponse>('archiveTrucking', apiPath.truckingArchive(), { body });
  },

  listTaxRates(): Promise<TaxRateListResponse> {
    return call<TaxRateListResponse>('listTaxRates', apiPath.taxRates());
  },
  createTaxRate(body: CreateTaxRateRequest): Promise<TaxRate> {
    return call<TaxRate>('createTaxRate', apiPath.taxRates(), { body });
  },
  // No deleteTaxRate: a tax rate row cannot be taken away, only superseded.

  listEquipmentRates(): Promise<EquipmentRateListResponse> {
    return call<EquipmentRateListResponse>('listEquipmentRates', apiPath.equipmentRates());
  },
  createEquipmentRate(body: CreateEquipmentRateRequest): Promise<EquipmentRate> {
    return call<EquipmentRate>('createEquipmentRate', apiPath.equipmentRates(), { body });
  },
  archiveEquipment(body: ArchiveEquipmentRequest): Promise<EquipmentRateListResponse> {
    return call<EquipmentRateListResponse>('archiveEquipment', apiPath.equipmentArchive(), { body });
  },

  listOperatorRates(): Promise<OperatorRateListResponse> {
    return call<OperatorRateListResponse>('listOperatorRates', apiPath.operatorRates());
  },
  createOperatorRate(body: CreateOperatorRateRequest): Promise<OperatorRate> {
    return call<OperatorRate>('createOperatorRate', apiPath.operatorRates(), { body });
  },
  archiveOperator(body: ArchiveOperatorRequest): Promise<OperatorRateListResponse> {
    return call<OperatorRateListResponse>('archiveOperator', apiPath.operatorArchive(), { body });
  },

  listCancellationFees(): Promise<CancellationFeeListResponse> {
    return call<CancellationFeeListResponse>('listCancellationFees', apiPath.cancellationFees());
  },
  createCancellationFee(body: CreateCancellationFeeRequest): Promise<CancellationFee> {
    return call<CancellationFee>('createCancellationFee', apiPath.cancellationFees(), { body });
  },
  // No deleteCancellationFee: there is only ever one fee to price with, so a
  // row cannot be taken away, only superseded.

  /* -------------------------------------------------- admin: expenses */

  listExpenses(month: string): Promise<ExpenseMonthResponse> {
    return call<ExpenseMonthResponse>(
      'listExpenses',
      `${apiPath.adminExpenses()}?month=${encodeURIComponent(month)}`
    );
  },
  createExpense(body: SaveExpenseRequest): Promise<Expense> {
    return call<Expense>('createExpense', apiPath.adminExpenses(), { body });
  },
  updateExpense(expenseId: number, body: SaveExpenseRequest): Promise<Expense> {
    return call<Expense>('updateExpense', apiPath.adminExpense(expenseId), { body });
  },
  deleteExpense(expenseId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteExpense', apiPath.adminExpense(expenseId));
  },

  listLeases(): Promise<LeaseListResponse> {
    return call<LeaseListResponse>('listLeases', apiPath.adminLeases());
  },
  createLease(body: SaveLeaseRequest): Promise<EquipmentLease> {
    return call<EquipmentLease>('createLease', apiPath.adminLeases(), { body });
  },
  updateLease(leaseId: number, body: SaveLeaseRequest): Promise<EquipmentLease> {
    return call<EquipmentLease>('updateLease', apiPath.adminLease(leaseId), { body });
  },
  deleteLease(leaseId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteLease', apiPath.adminLease(leaseId));
  },

  listExpenseCategories(): Promise<ExpenseCategoryListResponse> {
    return call<ExpenseCategoryListResponse>('listExpenseCategories', apiPath.expenseCategories());
  },
  createExpenseCategory(body: SaveExpenseCategoryRequest): Promise<ExpenseCategory> {
    return call<ExpenseCategory>('createExpenseCategory', apiPath.expenseCategories(), { body });
  },
  updateExpenseCategory(categoryId: number, body: SaveExpenseCategoryRequest): Promise<ExpenseCategory> {
    return call<ExpenseCategory>('updateExpenseCategory', apiPath.expenseCategory(categoryId), { body });
  },

  /* ------------------------------------------------ admin: statements */

  /**
   * Asks what the server already knows about these lines before anything is
   * written. The FILE is never sent — only the lines the browser read out of
   * it, and a fingerprint of its content so a re-import is noticed.
   */
  checkStatementLines(body: CheckStatementRequest): Promise<CheckStatementResponse> {
    return call<CheckStatementResponse>('checkStatementLines', apiPath.adminStatementCheck(), { body });
  },
  importStatement(body: ImportStatementRequest): Promise<StatementImportResponse> {
    return call<StatementImportResponse>('importStatement', apiPath.adminStatements(), { body });
  },
  listStatements(): Promise<StatementListResponse> {
    return call<StatementListResponse>('listStatements', apiPath.adminStatements());
  },
  getStatement(statementId: number): Promise<StatementDetailResponse> {
    return call<StatementDetailResponse>('getStatement', apiPath.adminStatement(statementId));
  },
  deleteStatement(statementId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteStatement', apiPath.adminStatement(statementId));
  },

  /* ------------------------------------------------------------ projects */

  listProjects(): Promise<ProjectListResponse> {
    return call<ProjectListResponse>('listProjects', apiPath.projects());
  },

  getProject(): Promise<ProjectResponse> {
    return call<ProjectResponse>('getProject', apiPath.project());
  },

  updateProject(body: UpdateProjectRequest): Promise<ProjectResponse> {
    return call<ProjectResponse>('updateProject', apiPath.project(), { body });
  },

  /* ----------------------------------------------------------- job setup */

  getJobSetup(): Promise<JobSetupResponse> {
    return call<JobSetupResponse>('getJobSetup', apiPath.jobSetup());
  },

  updateJobSetup(body: UpdateJobSetupRequest): Promise<JobSetupResponse> {
    return call<JobSetupResponse>('updateJobSetup', apiPath.jobSetup(), { body });
  },

  updateJobSetupItem(
    itemId: number,
    body: UpdateJobSetupItemRequest
  ): Promise<JobSetupItemResponse> {
    return call<JobSetupItemResponse>('updateJobSetupItem', apiPath.jobSetupItem(itemId), {
      body
    });
  },

  /* --------------------------------------------------------------- pours */

  listPours(): Promise<PourListResponse> {
    return call<PourListResponse>('listPours', apiPath.pours());
  },

  createPour(body: CreatePourRequest): Promise<PourResponse> {
    return call<PourResponse>('createPour', apiPath.pours(), { body });
  },

  getPour(pourId: number): Promise<PourResponse> {
    return call<PourResponse>('getPour', apiPath.pour(pourId));
  },

  updatePour(pourId: number, body: UpdatePourRequest): Promise<PourResponse> {
    return call<PourResponse>('updatePour', apiPath.pour(pourId), { body });
  },

  /** 409 `hold_point_open` carries `blockingItems` on the thrown ApiError. */
  closePour(pourId: number, note?: string | null): Promise<PourResponse> {
    return call<PourResponse>('closePour', apiPath.closePour(pourId), {
      body: { note: note ?? null }
    });
  },

  /* ----------------------------------------------------------- ITP items */

  updateItpItem(
    pourId: number,
    itemId: number,
    body: UpdateItpItemRequest
  ): Promise<ItpItemResponse> {
    return call<ItpItemResponse>('updateItpItem', apiPath.itpItem(pourId, itemId), { body });
  },

  /* --------------------------------------------------------- chute tests */

  listChuteTests(pourId: number): Promise<ChuteTestListResponse> {
    return call<ChuteTestListResponse>('listChuteTests', apiPath.chuteTests(pourId));
  },

  createChuteTest(pourId: number, body: CreateChuteTestRequest): Promise<ChuteTestResponse> {
    return call<ChuteTestResponse>('createChuteTest', apiPath.chuteTests(pourId), { body });
  },

  updateChuteTest(
    pourId: number,
    testId: number,
    body: UpdateChuteTestRequest
  ): Promise<ChuteTestResponse> {
    return call<ChuteTestResponse>('updateChuteTest', apiPath.chuteTest(pourId, testId), {
      body
    });
  },

  deleteChuteTest(pourId: number, testId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteChuteTest', apiPath.chuteTest(pourId, testId));
  },

  /* ---------------------------------------------------- stop-hold events */

  listStopHoldEvents(pourId: number): Promise<StopHoldEventListResponse> {
    return call<StopHoldEventListResponse>('listStopHoldEvents', apiPath.stopHoldEvents(pourId));
  },

  createStopHoldEvent(
    pourId: number,
    body: CreateStopHoldEventRequest
  ): Promise<StopHoldEventResponse> {
    return call<StopHoldEventResponse>('createStopHoldEvent', apiPath.stopHoldEvents(pourId), {
      body
    });
  },

  updateStopHoldEvent(
    pourId: number,
    eventId: number,
    body: UpdateStopHoldEventRequest
  ): Promise<StopHoldEventResponse> {
    return call<StopHoldEventResponse>(
      'updateStopHoldEvent',
      apiPath.stopHoldEvent(pourId, eventId),
      { body }
    );
  },

  deleteStopHoldEvent(pourId: number, eventId: number): Promise<OkResponse> {
    return call<OkResponse>('deleteStopHoldEvent', apiPath.stopHoldEvent(pourId, eventId));
  },

  /* --------------------------------------------------- end-of-pour items */

  listEndOfPourItems(pourId: number): Promise<EndOfPourItemListResponse> {
    return call<EndOfPourItemListResponse>('listEndOfPourItems', apiPath.endOfPourItems(pourId));
  },

  updateEndOfPourItem(
    pourId: number,
    itemId: number,
    body: UpdateEndOfPourItemRequest
  ): Promise<EndOfPourItemResponse> {
    return call<EndOfPourItemResponse>(
      'updateEndOfPourItem',
      apiPath.endOfPourItem(pourId, itemId),
      { body }
    );
  },

  /* -------------------------------------------------------------- photos */

  listPhotos(pourId: number): Promise<PhotoListResponse> {
    return call<PhotoListResponse>('listPhotos', apiPath.photos(pourId));
  },

  photoUploadUrl(pourId: number, body: PhotoUploadUrlRequest): Promise<PhotoUploadUrlResponse> {
    return call<PhotoUploadUrlResponse>('photoUploadUrl', apiPath.photoUploadUrl(pourId), {
      body
    });
  },

  attachPhoto(pourId: number, body: AttachPhotoRequest): Promise<PhotoResponse> {
    return call<PhotoResponse>('attachPhoto', apiPath.photos(pourId), { body });
  },

  /* -------------------------------------------------------- share tokens */

  listShareTokens(): Promise<ShareTokenListResponse> {
    return call<ShareTokenListResponse>('listShareTokens', apiPath.shareTokens());
  },

  createShareToken(body: CreateShareTokenRequest): Promise<ShareTokenResponse> {
    return call<ShareTokenResponse>('createShareToken', apiPath.shareTokens(), { body });
  },

  revokeShareToken(tokenId: number): Promise<ShareTokenResponse> {
    return call<ShareTokenResponse>('revokeShareToken', apiPath.revokeShareToken(tokenId));
  },

  /* --------------------------------------------------------- audit trail */

  listAudit(query?: { pour_id?: number; limit?: number }): Promise<AuditListResponse> {
    return call<AuditListResponse>('listAudit', apiPath.audit(query));
  }
} as const;

/* ==========================================================================
 * URLs the browser fetches directly (images, downloads)
 * ======================================================================== */

/** `<img src>` for a photo. R2 is never public; this streams through the API. */
export function photoContentUrl(photoId: number): string {
  return apiPath.photoContent(photoId);
}

/**
 * Download links. Available to crew AND client, per the spec.
 *
 * Every one of these is a FILE: the browser saves it, nothing opens in a tab
 * and nothing puts a print dialog up. `pourPdf` is the print-styled record
 * redrawn as a real PDF by the Worker, for a share link, an email, or a phone
 * where the in-app print button cannot run. `pourXlsx` and `projectXlsx` are
 * the CSVs' records in real Excel workbooks, with the photographs in the sheet
 * — a CSV cannot carry an image, and these are the files for a client who works
 * in spreadsheets. The `.csv` routes remain for anything automated; the buttons
 * that pointed at them are gone.
 */
export const exportUrl = {
  pourCsv: (pourId: number) => apiPath.exportPourCsv(pourId),
  pourXlsx: (pourId: number) => apiPath.exportPourXlsx(pourId),
  pourPdf: (pourId: number) => apiPath.exportPourPdf(pourId),
  projectCsv: () => apiPath.exportProjectCsv(),
  projectXlsx: () => apiPath.exportProjectXlsx()
} as const;

/* ==========================================================================
 * Photos
 * ==========================================================================
 *
 * The capture -> downscale -> upload -> attach pipeline lives in ./photo.ts,
 * not here. It needs a queue that survives a reload (IndexedDB), per-step
 * resume so a failed attach never re-uploads the bytes, and unbounded retry —
 * the spec is explicit that a photo is "queued and retried, and shows as
 * pending rather than silently vanishing". This module keeps only the raw
 * request functions it drives (`api.photoUploadUrl`, `api.attachPhoto`,
 * `api.listPhotos`) and `photoContentUrl` above.
 */
